import React, { useState, useMemo, useLayoutEffect, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import useVisitStore from '../../stores/visitStore';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import useAdmissionEnabled from '../../hooks/useAdmissionEnabled';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import ScanShareQueuePanel from '../abha/ScanShareQueuePanel';
import ScanShareQueueSync from '../abha/ScanShareQueueSync';
import useScanQueueStore from '../../stores/scanQueueStore';
import { hydrateDashboard } from '../../services/dashboardHydrator';
import { STAFF_ROLES, GREETING_ROLE_LABELS } from '../../constants/staff';
import { MONTHLY_COLLECTIONS_PERMISSIONS } from '../../constants/reports';
import { isAdminRole } from '../AdminRoleGate';
import { ADMISSION_PERMISSIONS } from '../../constants/admission';
import { ABDM_ANY_ABHA } from '../../constants/abdmPermissions';
import ChatLogo from '../common/ChatHeader/ChatLogo';
import ThemeToggleButton from '../common/ChatHeader/ThemeToggleButton';
import { UserProfilePanel } from '../common/UserProfile';
import RegisterVisitForm from '../visit/RegisterVisitForm';
import ManageVisitPage from '../visit/ManageVisitPage';
import PrescriptionTemplateManager from '../visit/PrescriptionTemplateManager';
import BookAppointmentForm from '../visit/BookAppointmentForm';
import ManageAppointmentsPage from '../visit/ManageAppointmentsPage';
import DoctorDashboard from '../visit/DoctorDashboard';
import ClinicCapacitySummary from '../visit/ClinicCapacitySummary';
import VisitDashboard from '../visit/VisitDashboard';
import PrescriptionBrandingSettings from '../settings/PrescriptionBrandingSettings';
import InvoiceProfileSettings from '../settings/InvoiceProfileSettings';
import SmartWorkflowWidget from './SmartWorkflowWidget';
import ConfirmAppointmentsWidget from './ConfirmAppointmentsWidget';
import { ConnectionStatusIndicator } from '../common/ConnectionStatusIndicator';
import { RealtimeToastNotifier } from '../common/RealtimeToastNotifier';
import { ROUTES } from '../../constants/routes';
import './AdminDashboard.css';
import { takeTransientAadhaar } from '../../utils/transientAadhaar';
import { ABDM_ICONS, MODULE_TITLES, ABHA_CARD, OTHER_ORGS_TITLE, OTHER_ORGS } from '../../constants/abdmCopy';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * AdminDashboard — Responsive admin homepage
 * Desktop (4-col) → Tablet (3-col) → Mobile (2-col)
 * ═══════════════════════════════════════════════════════════════════════
 */

const MODULE_CARDS = [
  // NOTE: "Fees & Billing" is rendered separately below as a navigate() card
  // (it opens a multi-tab full-page at /org/fees-billing) — not a MODULE_CARDS
  // entry. The card was moved out so all three tabs (Consultation / Procedure
  // Catalog / Doctor Rates) share one entry point.
  {
    id: 'rx-branding',
    title: 'Rx Branding',
    subtitle: 'Prescription look & feel',
    icon: '🎨',
    permission: 'MANAGE_BRANDING',
    gradient: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
    glowColor: 'rgba(142, 68, 173, 0.15)',
    component: 'PrescriptionBrandingSettings',
    moduleType: 'form',
  },
  {
    id: 'rx-templates',
    title: 'Rx Templates',
    subtitle: 'Saved prescriptions',
    icon: '💊',
    permission: 'MANAGE_RX_TEMPLATES',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    glowColor: 'rgba(13, 148, 136, 0.15)',
    component: 'PrescriptionTemplateManager',
    moduleType: 'list',
  },
  // Round-2 T-INV1-FE — org Invoice/Billing profile (Ruling 4 / story INV-1).
  // Admin-only (MANAGE_FEES, same ADMIN-only slug as Fees & Billing) so it
  // sits alongside Rx Branding as another org-settings "form" module.
  {
    id: 'invoice-profile',
    title: 'Invoice / Billing Profile',
    subtitle: 'Legal name, GSTIN, address & logo for invoices',
    icon: '🧾',
    permission: 'MANAGE_FEES',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #059669 100%)',
    glowColor: 'rgba(5, 150, 105, 0.15)',
    component: 'InvoiceProfileSettings',
    moduleType: 'form',
  },
  {
    id: 'register-visit',
    title: 'Register Visit',
    subtitle: 'New patient walk-in',
    icon: '📝',
    permission: 'CREATE_VISIT',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
    glowColor: 'rgba(13, 148, 136, 0.15)',
    component: 'RegisterVisitForm',
    moduleType: 'form',
  },
  {
    id: 'book-appointment',
    title: 'Book Appointment',
    subtitle: 'Schedule future visit',
    icon: '📅',
    permission: 'MANAGE_APPOINTMENT',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #0d9488 100%)',
    glowColor: 'rgba(13, 148, 136, 0.15)',
    component: 'BookAppointmentForm',
    moduleType: 'form',
  },
  {
    id: 'queue-dashboard',
    title: 'Queue Dashboard',
    subtitle: 'Doctor-wise capacity',
    icon: '🩺',
    permission: 'VIEW_QUEUE',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
    glowColor: 'rgba(15, 118, 110, 0.15)',
    component: 'DoctorDashboard',
    moduleType: 'list',
  },
  {
    id: 'visit-dashboard',
    title: 'Visit Dashboard',
    subtitle: "Today's visit overview",
    icon: '📊',
    permission: 'VIEW_VISIT_DASHBOARD',
    gradient: 'linear-gradient(135deg, #115e59 0%, #0d9488 100%)',
    glowColor: 'rgba(79, 70, 229, 0.15)',
    component: 'VisitDashboard',
    moduleType: 'list',
  },
  {
    id: 'manage-visits',
    title: 'Manage Visits',
    subtitle: 'Active visits & actions',
    icon: '📋',
    permission: 'VIEW_VISIT',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
    glowColor: 'rgba(37, 99, 235, 0.15)',
    component: 'ManageVisitPage',
    moduleType: 'list',
  },
  {
    id: 'manage-appointments',
    title: 'Manage Appointments',
    subtitle: 'Today & future bookings',
    icon: '📆',
    permission: 'MANAGE_APPOINTMENT',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
    glowColor: 'rgba(168, 85, 247, 0.15)',
    component: 'ManageAppointmentsPage',
    moduleType: 'list',
  },
];

// LAB-ASSIGN-2 / FE-2 — cards hidden from LAB_TECHNICIAN (§10.6) and the
// frozen Register-Lab-&-Diagnostic-Visit relabel for that role's 'register-visit' card.
const LAB_TECH_HIDDEN_CARD_IDS = ['queue-dashboard', 'manage-visits'];
const LAB_TECH_REGISTER_VISIT_LABEL = {
  title: 'Register Lab & Diagnostic Visit',
  subtitle: 'Register a walk-in patient for tests.',
};

const COMPONENT_MAP = {
  PrescriptionTemplateManager,
  RegisterVisitForm,
  ManageVisitPage,
  BookAppointmentForm,
  ManageAppointmentsPage,
  DoctorDashboard,
  VisitDashboard,
  PrescriptionBrandingSettings,
  InvoiceProfileSettings,
};

export default function AdminDashboard({ onManageStaff, onStaffLogin, sessionData }) {
  const { role, hasPermission } = usePermissions();
  const { isScoped, scopeLabel, memberId } = useScope();
  // C2 — Occupancy Board card shows only when the Admission module is on AND the
  // viewer holds VIEW_OCCUPANCY_BOARD (route stays gated by AdmissionEnabledGate).
  const admissionOn = useAdmissionEnabled();
  const { isDark } = useTheme();
  const setScope = useVisitStore((s) => s.setScope);
  const [activeModule, setActiveModule] = useState(null);
  const { show: abdmOn } = useAbdmEnabled(); // org ABDM mode DISABLED (or not yet known) hides every ABDM surface
  const canScanShare = abdmOn && hasPermission('ABHA_SCAN_SHARE');
  const scanWaiting = useScanQueueStore((s) => (s.entries ? s.entries.length : 0));
  const [activeModuleType, setActiveModuleType] = useState(null);
  const [focusVisitId, setFocusVisitId] = useState(null);
  const [initialAbhaSnapshot, setInitialAbhaSnapshot] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Initialize Realtime Sync — once at dashboard level ──
  const { connectionStatus, syncMode } = useRealtimeSync();

  // ── Set store scope BEFORE child effects fire ──────────────────
  // useLayoutEffect runs before any child useEffect, ensuring
  // fetchDailyVisits() calls in children (ClinicCapacitySummary,
  // SmartWorkflowWidget) already have the correct doctor scope.
  // Scoped roles (DOCTOR) → inject memberId; unscoped (ADMIN) → clear to null
  useLayoutEffect(() => {
    setScope(isScoped ? memberId : null);
  }, [isScoped, memberId, setScope]);

  // ── Hydrate the dashboard from ONE request — once on mount ─────
  // Loads /organisation/dashboard-summary and populates the visit +
  // sitting stores, so the child widgets piggyback on it instead of each
  // firing their own fetch (the cause of the refresh 429s). Runs in a
  // useLayoutEffect so the slice keys are registered BEFORE child
  // useEffects fire. Safe-by-default: on failure widgets fall back to
  // their own fetches (see services/dashboardHydrator.js).
  useLayoutEffect(() => {
    hydrateDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE: No periodic polling here — RealtimeSyncService handles:
  //   • WS push events (instant)
  //   • 60s reconciliation poll (safety-net)
  //   • Tab-visibility reconcile on return

  const visibleCards = useMemo(
    () => {
      /* Scope-aware title/subtitle overrides for scoped roles (e.g. DOCTOR) */
      const SCOPED_OVERRIDES = {
        'manage-visits':        { title: `${scopeLabel} Visits`,        subtitle: 'Your active visits' },
        'manage-appointments':  { title: 'My Appointments',             subtitle: 'Your scheduled appointments' },
        'queue-dashboard':      { title: `${scopeLabel} Queue`,         subtitle: 'Your patient queue' },
        'visit-dashboard':      { title: `${scopeLabel} Stats`,         subtitle: 'Your visit overview' },
      };
      const isLabTechnician = (role || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN;
      return MODULE_CARDS.filter((card) => hasPermission(card.permission))
        // LAB-ASSIGN-2 / FE-2 — the technician's queue/visit management lives
        // in their own lab-queue widget, not these clinic-wide surfaces.
        .filter((card) => !(isLabTechnician && LAB_TECH_HIDDEN_CARD_IDS.includes(card.id)))
        .map((card) => {
          // Kept OUT of SCOPED_OVERRIDES above: DOCTOR is also a scoped role
          // and holds CREATE_VISIT — the lab relabel must not leak onto the
          // doctor's Register Visit card.
          if (isLabTechnician && card.id === 'register-visit') {
            return { ...card, ...LAB_TECH_REGISTER_VISIT_LABEL };
          }
          if (isScoped && SCOPED_OVERRIDES[card.id]) {
            return { ...card, ...SCOPED_OVERRIDES[card.id] };
          }
          return card;
        });
    },
    [hasPermission, isScoped, scopeLabel, role]
  );

  // S2 — the Collections tile is ROUTE-based, so it is not a MODULE_CARDS
  // entry and never appears in `visibleCards`. An ACCOUNTANT holds ONLY this
  // tile: without counting it, the dashboard would render the Collections
  // card AND the "No Modules Available" panel directly underneath it.
  const canViewCollections = hasPermission(
    MONTHLY_COLLECTIONS_PERMISSIONS.VIEW_MONTHLY_COLLECTIONS,
  );

  // EPIC PATIENT-CARD — the Patients tile is ADMIN-only by ROLE, never by a
  // permission slug: ADMIN holds FULL_ACCESS server-side, which short-circuits
  // every permission check, so no slug can express "admin but not doctor"
  // (D7). Do NOT "fix" this to hasPermission(...) to match its neighbours.
  // isAdminRole() is the same predicate the route guard uses (AdminRoleGate),
  // so the tile and the route can never disagree — and it fails closed on the
  // null role an unmappable session now derives to (bk-793 / F2).
  const canViewPatientCard = isAdminRole(role);

  const greeting = useMemo(() => {
    const name = sessionData?.displayName || sessionData?.username || '';
    // F6 — sourced from the shared GREETING_ROLE_LABELS (constants/staff.js,
    // extends STAFF_ROLE_LABELS) rather than a second inline role->label
    // map, so a new role (e.g. ACCOUNTANT) never needs a matching literal
    // added here too. bk-444 — GREETING_ROLE_LABELS additionally covers
    // PHARMACY (real login-group-derived role, not staff-screen-creatable;
    // never added to STAFF_ROLES/STAFF_ROLE_LABELS/STAFF_ROLE_OPTIONS).
    return {
      name: name || 'there',
      role: GREETING_ROLE_LABELS[role] || 'User',
    };
  }, [sessionData, role]);

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const headerBg = isDark ? 'rgba(14,14,15,0.85)' : 'rgba(255,255,255,0.85)';
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const headerShadow = isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.06)';

  const closeModule = () => { setActiveModule(null); setActiveModuleType(null); setFocusVisitId(null); setInitialAbhaSnapshot(null); };

  const openModule = (componentName, moduleType, visitId) => {
    setFocusVisitId(visitId || null);
    setActiveModule(componentName);
    setActiveModuleType(moduleType || 'list');
  };

  // Deep-link support: components anywhere in the tree can call
  // navigate('/org#visit=<id>') to open ManageVisitPage focused on a
  // specific visit. Picks up the hash on first render and on subsequent
  // hashchange events. Clears the hash after consuming it so re-opening
  // the dashboard doesn't re-trigger.
  useEffect(() => {
    const consumeHash = () => {
      const hash = window.location.hash || '';
      const m = hash.match(/#visit=([A-Za-z0-9-]+)/);
      if (!m) return;
      const visitId = m[1];
      openModule('ManageVisitPage', 'list', visitId);
      // Strip the hash so navigating back into /org doesn't loop.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    };
    consumeHash();
    window.addEventListener('hashchange', consumeHash);
    return () => window.removeEventListener('hashchange', consumeHash);
    // openModule is stable — defined inside this component but only
    // touches setState calls. Linting it would force a useCallback hoist
    // for no behavior gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Deep-link from ABHAPage: openRegister=true → open RegisterVisitForm ──
  useEffect(() => {
    if (!location.state?.openRegister) return;
    const handedOff = location.state.abhaSnapshot;
    // Re-attach the Aadhaar that ABHAPage kept out of router state (one-shot, in-memory).
    setInitialAbhaSnapshot(handedOff ? { ...handedOff, aadhaar: takeTransientAadhaar() } : null);
    openModule('RegisterVisitForm', 'form');
    // Clear router state so back navigation doesn't re-trigger.
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Active Module View ─────────────────────────────
  if (activeModule) {
    const ActiveComponent = COMPONENT_MAP[activeModule];
    if (ActiveComponent) {
      return (
        <div className="admin-dashboard" style={{ background: bg }}>
          {/* ── Branded Header (same as main dashboard) ── */}
          <header
            className="admin-header"
            style={{
              background: headerBg,
              borderBottom: `1px solid ${borderColor}`,
              boxShadow: headerShadow,
            }}
          >
            <div className="admin-header-inner">
              <div className="admin-header-left">
                <div className="logo-container">
                  <ChatLogo isLoading={false} />
                </div>
                <button
                  className="admin-back-btn"
                  onClick={() => { closeModule(); }}
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    color: isDark ? '#5eead4' : '#0f766e',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                    padding: '0.3rem 0.625rem',
                    borderRadius: '0.5rem',
                    border: '1px solid',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ← Dashboard
                </button>
              </div>
              <div className="admin-header-right">
                <ThemeToggleButton />
                <UserProfilePanel />
              </div>
            </div>
          </header>

          <div className="admin-module-wrapper">
            <div className={activeModuleType === 'form' ? 'admin-module-form' : 'admin-module-list'}>
              <ActiveComponent
                onBack={() => { closeModule(); }}
                onSuccess={() => { closeModule(); }}
                onCancel={() => { closeModule(); }}
                onOpenModule={openModule}
                initialFocusVisitId={focusVisitId}
                initialAbhaSnapshot={activeModule === 'RegisterVisitForm' ? initialAbhaSnapshot : undefined}
              />
            </div>
          </div>
        </div>
      );
    }
  }

  // ── Main Dashboard ─────────────────────────────────
  return (
    <div className="admin-dashboard" style={{ background: bg }}>
      {/* ── Branded Header ────────────────────────── */}
      <header
        className="admin-header"
        style={{
          background: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          boxShadow: headerShadow,
        }}
      >
        <div className="admin-header-inner">
          {/* Left: Logo + Greeting */}
          <div className="admin-header-left">
            <div className="logo-container">
              <ChatLogo isLoading={false} />
            </div>
            <div>
              <h1
                className="admin-greeting-title"
                style={{ color: isDark ? '#f5f5f7' : '#111827' }}
              >
                Hello, {greeting.name} 👋
              </h1>
              <p
                className="admin-greeting-subtitle"
                style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
              >
                {greeting.role} Dashboard
              </p>
              <ConnectionStatusIndicator status={connectionStatus} syncMode={syncMode} />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="admin-header-right">
            <ThemeToggleButton />
            <UserProfilePanel />
          </div>
        </div>
      </header>

      {/* ── Dashboard Content ─────────────────────── */}
      <main className="admin-main">
        {/* Clinic Capacity Summary — a clinic-wide doctor-queue overview, so
            hide it from a lab technician (their work is the lab queue below). */}
        {(hasPermission('VIEW_QUEUE') || hasPermission('VIEW_VISIT_DASHBOARD'))
          && (role || '').toUpperCase() !== STAFF_ROLES.LAB_TECHNICIAN && (
          <div className="admin-capacity-wrapper">
            <ClinicCapacitySummary />
          </div>
        )}

        {/* R154 - the RECEPTIONIST keeps the compact waiting list inline; admin sees only the count pill on
            the ABHA card (list is on the ABHA page). Both share the one queue store; the panel mounts the poller. */}
        {abdmOn && (role || '').toUpperCase() === STAFF_ROLES.RECEPTIONIST && (
          <ScanShareQueuePanel
            onRegisterOpd={(snapshot) => {
              setInitialAbhaSnapshot(snapshot);
              openModule('RegisterVisitForm', 'form');
            }}
          />
        )}

        {/* Smart Workflow Widget — role-aware live CTAs */}
        {(hasPermission('VIEW_QUEUE') || hasPermission('VIEW_VISIT_DASHBOARD') || hasPermission('CREATE_VISIT')) && (
          <div className="admin-capacity-wrapper">
            <SmartWorkflowWidget
              onNavigateToVisit={(visitId) => openModule('ManageVisitPage', 'list', visitId)}
              onOpenRegister={() => openModule('RegisterVisitForm', 'form')}
              onNavigateToAppointments={() => openModule('ManageAppointmentsPage', 'list')}
            />
          </div>
        )}

        {/* Confirm Appointments — floating purple counter badge (fixed overlay;
            self-hides when there are no impacts). Receptionist-only. */}
        {role === 'RECEPTIONIST' && (
          <ConfirmAppointmentsWidget
            onReschedule={() => openModule('ManageAppointmentsPage', 'list')}
            onNavigateToAppointments={() => openModule('ManageAppointmentsPage', 'list')}
          />
        )}

        {/* Section Label */}
        <h2
          className="admin-section-label"
          style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af' }}
        >
          Quick Actions
        </h2>

        {/* Module Cards Grid */}
        <div className="admin-card-grid">
          {/* Fees & Billing — rendered FIRST so it's the top-left card */}
          {hasPermission('MANAGE_STAFF') && (
            <ModuleCard
              card={{
                id: 'fees-billing',
                title: 'Fees & Billing',
                subtitle: 'Consultation · Procedures · Rate overrides',
                icon: '₹',
                gradient: 'linear-gradient(135deg, #0d9488 0%, #059669 100%)',
                glowColor: 'rgba(5, 150, 105, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.FEES_BILLING)}
            />
          )}

          {/* S2 — Reports & Accounts (IPD revenue + discharge register).
              Gated ONLY on VIEW_MONTHLY_COLLECTIONS — deliberately NOT
              behind useAdmissionEnabled() nor any role (§2.4: an OPD-only
              org must reach this card too, and sees the empty state).
              MICROCOPY M1 — "Reports & Accounts" is the owner-ruled name;
              never "Collections" or "Monthly Reports" (no collections
              figure ships in Increment 1). */}
          {canViewCollections && (
            <ModuleCard
              card={{
                id: 'monthly-collections',
                title: 'Reports & Accounts',
                subtitle: 'IPD revenue and discharge register, by month',
                icon: '📈',
                gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                glowColor: 'rgba(124, 58, 237, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.MONTHLY_COLLECTIONS)}
            />
          )}

          {/* Procedure Queue — doctor/nurse assignee queue + doctor-as-approver */}
          {(hasPermission('EXECUTE_SITTING') || hasPermission('APPROVE_SITTING')) && (
            <ModuleCard
              card={{
                id: 'procedure-queue',
                title: 'Procedure Queue',
                subtitle: 'Your sittings · pay, start, approve',
                icon: '💉',
                gradient: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                glowColor: 'rgba(6, 182, 212, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.PROCEDURE_QUEUE)}
            />
          )}

          {/* Phase 2.7 — Treatment Plans authoring (DOCTOR + ADMIN only) */}
          {hasPermission('DRAFT_TREATMENT_PLAN') && (
            <ModuleCard
              card={{
                id: 'treatment-plans',
                title: 'Treatment Plans',
                subtitle: 'Author & manage reusable workflow plans',
                icon: '📘',
                gradient: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                glowColor: 'rgba(79, 70, 229, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.TREATMENT_PLANS)}
            />
          )}

          {/* Action Pending — receptionist + doctor dual-collector hub */}
          {hasPermission('COLLECT_PAYMENT') && (
            <ModuleCard
              card={{
                id: 'action-pending',
                title: 'Action Pending',
                subtitle: 'Items awaiting your action',
                icon: '💳',
                gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                glowColor: 'rgba(245, 158, 11, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.ACTION_PENDING)}
            />
          )}

          {/* ABHA — create / verify / link health IDs (M1) */}
          {abdmOn && ABDM_ANY_ABHA.some(hasPermission) && (
            <ModuleCard
              card={{
                id: 'abha',
                title: ABHA_CARD.title,
                subtitle: ABHA_CARD.subtitle,
                icon: ABDM_ICONS.ABHA,
                gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
                glowColor: 'rgba(3, 105, 161, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.ABHA)}
              pill={canScanShare && scanWaiting > 0 ? {
                text: `Scan & Share: ${scanWaiting} waiting`,
                onClick: () => navigate(ROUTES.ABHA, { state: { openTab: 'scan' } }),
              } : undefined}
            />
          )}
          {/* Keeps the pill count live for roles with no inline list (admin); never mounted for a doctor (no fetch). */}
          {canScanShare && <ScanShareQueueSync />}

          {/* ABDM HIP — publish this clinic's own visit records outward (M2).
              Automatic visit-completion publishing defaults off; this is the primary
              publish path, not a backfill tool. */}
          {abdmOn && hasPermission('HIP_CARE_CONTEXT') && (
            <ModuleCard
              card={{
                id: 'hip-care-contexts',
                title: MODULE_TITLES.LINK_RECORDS,
                subtitle: 'Link · Status · Retry',
                icon: ABDM_ICONS.LINK_RECORDS,
                gradient: 'linear-gradient(135deg, #be185d 0%, #db2777 100%)',
                glowColor: 'rgba(190, 24, 93, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.HIP_CARE_CONTEXTS)}
            />
          )}

          {/* Health Information — external patient history under consent (M3 HIU).
              Single card into the list page, which has its own "+ New Request"
              entry point for creating a consent request — consolidated from the
              earlier two-card (create/view) split since the list already covers both. */}
          {abdmOn && (hasPermission('HIU_CONSENT_REQUEST') || hasPermission('HIU_VIEW_RECORDS')) && (
            <ModuleCard
              card={{
                id: 'hiu-consents-list',
                title: OTHER_ORGS_TITLE,
                subtitle: OTHER_ORGS.cardSubtitle,
                icon: ABDM_ICONS.OTHER_ORGS,
                gradient: 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)',
                glowColor: 'rgba(14, 116, 144, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.HIU_CONSENTS)}
            />
          )}

          {/* EPIC PATIENT-CARD — Patients directory + 360° card. Role-gated,
              see the canViewPatientCard note above. */}
          {canViewPatientCard && (
            <ModuleCard
              card={{
                id: 'patients',
                title: 'Patients',
                subtitle: 'Search patients and open their full record',
                icon: '🗂',
                gradient: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
                glowColor: 'rgba(2, 132, 199, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.PATIENT_DIRECTORY)}
            />
          )}

          {visibleCards.map((card) => (
            <ModuleCard
              key={card.id}
              card={card}
              isDark={isDark}
              onClick={() => openModule(card.component, card.moduleType)}
            />
          ))}

          {/* Organisation Module Cards */}
          {hasPermission('MANAGE_STAFF') && (
            <ModuleCard
              card={{
                id: 'manage-staff',
                title: 'Manage Staff',
                subtitle: 'Add & manage team members',
                icon: '👥',
                gradient: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)',
                glowColor: 'rgba(234, 88, 12, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.MANAGE_STAFF)}
            />
          )}
          {hasPermission('MANAGE_DEPARTMENT') && (
            <ModuleCard
              card={{
                id: 'departments',
                title: 'Departments',
                subtitle: 'Manage departments & staff assignment',
                icon: '🏢',
                gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                glowColor: 'rgba(13, 148, 136, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.DEPARTMENTS)}
            />
          )}
          {/* C — Admission (IPD) entry points. Each gated on the org admission
              flag AND its own permission, mirroring the Occupancy Board card /
              the OPD "Register Visit" card. Route stays behind AdmissionEnabledGate. */}
          {admissionOn && hasPermission('ADMIT_PATIENT') && (
            <ModuleCard
              card={{
                id: 'register-admission',
                title: 'Register Admission',
                subtitle: 'Admit a patient (IPD)',
                icon: '🏥',
                gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                glowColor: 'rgba(13, 148, 136, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.ADMIT_PATIENT)}
            />
          )}
          {admissionOn && hasPermission('VIEW_ADMISSION') && (
            <ModuleCard
              card={{
                id: 'admission-desk',
                title: 'Manage Admission',
                subtitle: 'Active admissions & actions',
                icon: '🛎',
                gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                glowColor: 'rgba(15, 118, 110, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.ADMISSIONS)}
            />
          )}
          {admissionOn && hasPermission('VIEW_OCCUPANCY_BOARD') && (
            <ModuleCard
              card={{
                id: 'occupancy-board',
                title: 'Occupancy Board',
                subtitle: 'Live ward & bed occupancy',
                icon: '🛏',
                gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                glowColor: 'rgba(8, 145, 178, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.OCCUPANCY_BOARD)}
            />
          )}
          {/* C6b — the IPD tile. Diagnostics is its first content, never its
              identity (D27) — the title stays "IPD" so a second role's view
              can drop in behind it later without a rename. Icon/subtitle/
              gradient match the approved mockup (ipd-home-tile.html)
              verbatim. */}
          {admissionOn && (hasPermission(ADMISSION_PERMISSIONS.ACCESS_IPD)
            || hasPermission(ADMISSION_PERMISSIONS.VIEW_CLINICAL_RECORD)) && (
            <ModuleCard
              card={{
                id: 'ipd',
                title: 'IPD',
                subtitle: 'Find an admitted patient',
                icon: '🏨',
                gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                glowColor: 'rgba(15, 118, 110, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.IPD)}
            />
          )}
          {hasPermission('MANAGE_STAFF') && onStaffLogin && (
            <ModuleCard
              card={{
                id: 'staff-login',
                title: 'Staff Credentials',
                subtitle: 'View login details',
                icon: '🔑',
                gradient: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)',
                glowColor: 'rgba(180, 83, 9, 0.15)',
              }}
              isDark={isDark}
              onClick={onStaffLogin}
            />
          )}
          {/* Doctor Public Profile — hidden, continuing later
          {hasPermission('FUTURE_DEVELOPMENT') && (
            <ModuleCard
              card={{
                id: 'doctor-profile',
                title: 'Doctor Profile',
                subtitle: 'Manage public profile',
                icon: '🪪',
                gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                glowColor: 'rgba(8, 145, 178, 0.15)',
              }}
              isDark={isDark}
              onClick={() => navigate(ROUTES.DOCTOR_PROFILE)}
            />
          )} */}

          {/* REVIEW-FORM / R1 — "Rate ArogyaKhata". Deliberately UNGATED
              (visible to every role, not run through MODULE_CARDS'
              permission filter) — every authenticated org member may
              submit a review server-side too (no permission slug, D4/D11).
              Placed LAST in the grid. */}
          <ModuleCard
            card={{
              id: 'rate-arogyakhata',
              title: 'Rate ArogyaKhata',
              subtitle: 'Tell us how the app is working for you',
              icon: '⭐',
              gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
              glowColor: 'rgba(245,158,11,0.15)',
            }}
            isDark={isDark}
            onClick={() => navigate(ROUTES.REVIEW)}
          />
        </div>

        {/* Empty state — counts the route-based Collections tile too, so a
            role whose ONLY tile is Collections never sees "No Modules
            Available" sitting under a card it can plainly click. */}
        {visibleCards.length === 0 && !canViewCollections && (
          <div
            style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              borderRadius: '1.25rem',
              background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
              border: `1px solid ${borderColor}`,
            }}
          >
            <span style={{ fontSize: '3rem' }}>🔒</span>
            <h3
              style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: isDark ? '#e8e9ed' : '#111827',
                marginTop: '1rem',
              }}
            >
              No Modules Available
            </h3>
            <p
              style={{
                fontSize: '0.875rem',
                color: isDark ? '#9ca3af' : '#6b7280',
                marginTop: '0.5rem',
              }}
            >
              Contact your administrator for access permissions.
            </p>
          </div>
        )}
      </main>

      {/* ── Realtime Toast Notifications ──────────── */}
      <RealtimeToastNotifier />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * ModuleCard — Glassmorphic interactive card
 * ═══════════════════════════════════════════════════════════════════════ */
function ModuleCard({ card, isDark, onClick, pill }) {
  const [hovered, setHovered] = useState(false);

  const cardButton = (
    <button
      className="admin-module-card"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...(pill ? { paddingBottom: '2.5rem' } : {}),
        borderColor: hovered
          ? isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'
          : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        background: isDark
          ? hovered ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)'
          : hovered ? '#fff' : 'rgba(255,255,255,0.7)',
        boxShadow: hovered
          ? isDark
            ? `0 8px 32px rgba(0,0,0,0.4), 0 0 24px ${card.glowColor}`
            : `0 8px 32px rgba(0,0,0,0.08), 0 0 24px ${card.glowColor}`
          : isDark
            ? '0 2px 8px rgba(0,0,0,0.2)'
            : '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      {/* Icon */}
      <div
        className="admin-card-icon"
        style={{
          background: card.gradient,
          boxShadow: `0 4px 12px ${card.glowColor}`,
        }}
      >
        {card.icon}
      </div>

      {/* Title */}
      <h3
        style={{
          margin: 0,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: isDark ? '#f5f5f7' : '#111827',
          lineHeight: 1.3,
        }}
      >
        {card.title}
      </h3>

      {/* Subtitle */}
      <p
        style={{
          margin: '0.25rem 0 0',
          fontSize: '0.75rem',
          color: isDark ? '#9ca3af' : '#6b7280',
          lineHeight: 1.4,
        }}
      >
        {card.subtitle}
      </p>

      {/* Arrow indicator */}
      <div
        className="admin-card-arrow"
        style={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' }}
      >
        Open →
      </div>
    </button>
  );

  if (!pill) return cardButton;
  // The pill is a SIBLING of the card button (a button inside a button is invalid HTML), overlaid bottom-left.
  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      {cardButton}
      <button
        type="button"
        data-testid="scan-share-pill"
        onClick={pill.onClick}
        className="focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-teal-500"
        style={{
          position: 'absolute', left: '1rem', bottom: '0.75rem', padding: '2px 10px', borderRadius: 999,
          border: 'none', background: '#0f766e', color: '#fff', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
        }}
      >
        {pill.text}
      </button>
    </div>
  );
}
